import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import JSZip from 'jszip';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { extractBundlePdfData, useSignedPdfUpload } from 'libs.consent.react';
import { useFeatureFlags } from 'libs.flags.react';
import { Input, ModalWindow, Spinner, Text, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { SITE_FEATURE_FLAGS } from '../../constants/feature_flags.constants';
import { getApiErrorMessage, getApiInstance } from '../../helpers/api.helpers';
import { useAxonAccountContext } from '../../hooks/use_axon_account_context';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
const ECONSENT_BUNDLE_HOST_MESSAGE_TAG = '__econsent_bundle_host__';
const ECONSENT_BUNDLE_HOST_RESPONSE_TAG = '__econsent_bundle_host_response__';
const DOCUMENT_DETAIL_PATHS = [
    '_id',
    'created',
    'ec__accepted_signers.ec__account',
    'ec__accepted_signers.ec__signer_role',
    'ec__accepted_signers.ec__status',
    'ec__document_template.ec__title',
    'ec__document_template.ec__requested_data',
    'ec__document_template.ec__requested_signatures',
    'ec__document_template.ec__signer_roles',
    'ec__published_studio_template',
    'ec__published_studio_template.ec__bundle',
    'ec__published_studio_template.ec__requested_data',
    'ec__published_studio_template.ec__requested_signatures',
    'ec__published_studio_template.ec__signer_roles',
    'ec__required_data',
    'ec__signatures',
    'ec__status',
];
const TEXT_EXTENSIONS = ['html', 'js', 'css', 'json', 'txt', 'map', 'svg'];
const toArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }
    return value?.data || [];
};
const getSignerAccountId = (signer) => typeof signer.ec__account === 'string' ? signer.ec__account : signer.ec__account?._id;
const getRuntimeSignatures = (documentDetail) => documentDetail?.ec__published_studio_template?.ec__requested_signatures ||
    documentDetail?.ec__document_template?.ec__requested_signatures ||
    [];
const getRuntimeData = (documentDetail) => documentDetail?.ec__published_studio_template?.ec__requested_data ||
    documentDetail?.ec__document_template?.ec__requested_data ||
    [];
const getRuntimeSignerRoles = (documentDetail) => documentDetail?.ec__published_studio_template?.ec__signer_roles ||
    documentDetail?.ec__document_template?.ec__signer_roles ||
    [];
const getSignedSignatureIdentifiers = (documentDetail) => new Set(toArray(documentDetail?.ec__signatures)
    .map((signature) => signature.value?.ec__signature_identifier)
    .filter(Boolean));
const getSignedSignatureSummaries = (documentDetail) => toArray(documentDetail?.ec__signatures)
    .map((signature) => ({
    ec__key: signature.value?.ec__signature_identifier || '',
    name: signature.signer || '',
    id: signature._id,
    date: signature.date,
}))
    .filter((signature) => signature.ec__key && signature.name);
const getCompletedDataIdentifiers = (documentDetail) => new Set(toArray(documentDetail?.ec__required_data)
    .map((datum) => datum.ec__identifier)
    .filter(Boolean));
const getBundleFileExtension = (fileName) => fileName.split('.').pop()?.toLowerCase();
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const normalizeRole = (role) => role?.trim().toLowerCase();
const getDisplayTitle = (title) => title.split(/[\\/]/).filter(Boolean).pop() || title;
const getMimeType = (extension) => {
    switch (extension) {
        case 'png':
            return 'image/png';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'gif':
            return 'image/gif';
        case 'webp':
            return 'image/webp';
        case 'svg':
            return 'image/svg+xml';
        case 'html':
            return 'text/html';
        case 'css':
            return 'text/css';
        case 'js':
            return 'text/javascript';
        case 'json':
            return 'application/json';
        case 'pdf':
            return 'application/pdf';
        default:
            return 'text/plain';
    }
};
const replaceBundleReferences = (source, urlByFileName) => Object.keys(urlByFileName)
    .sort((a, b) => b.length - a.length)
    .reduce((result, fileName) => {
    return [`./${fileName}`, `/${fileName}`, fileName].reduce((updatedResult, reference) => updatedResult.replace(new RegExp(escapeRegExp(reference), 'g'), urlByFileName[fileName]), result);
}, source);
const roleMatches = (a, b) => !!a && !!b && normalizeRole(a) === normalizeRole(b);
const findSignatureByKey = (documentDetail, signatureKey) => getRuntimeSignatures(documentDetail).find((signature) => signature.ec__key === signatureKey);
const bytesToBase64 = (bytes) => {
    let binary = '';
    for (let index = 0; index < bytes.length; index += 1) {
        binary += String.fromCharCode(bytes[index]);
    }
    return btoa(binary);
};
const blobToDataUrl = async (blob) => {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    return `data:${blob.type || 'application/octet-stream'};base64,${bytesToBase64(bytes)}`;
};
const stringToDataUrl = (source, type) => blobToDataUrl(new Blob([source], { type }));
const isEconsentBundleHostMessage = (value) => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const message = value;
    if (message[ECONSENT_BUNDLE_HOST_MESSAGE_TAG] !== true) {
        return false;
    }
    if (message.kind === 'cancel' || message.kind === 'track' || message.kind === 'sign') {
        return true;
    }
    if (message.kind !== 'request-name') {
        return false;
    }
    const request = message.request;
    return typeof message.requestId === 'string' && !!message.requestId && typeof request?.ec__key === 'string';
};
const buildEconsentBundleNameResponse = (requestId, name) => ({
    [ECONSENT_BUNDLE_HOST_RESPONSE_TAG]: true,
    kind: 'name-response',
    requestId,
    name,
});
const getHostHooksCode = (allowedSignerRole, signedSignatures) => {
    const signedSignatureSeed = signedSignatures
        ? `window.__econsent_signed_signatures__ = ${JSON.stringify(signedSignatures).replace(/</g, '\\u003c')};`
        : '';
    return `
    (function () {
      ${signedSignatureSeed}

      var pendingNameRequests = Object.create(null);
      var nextRequestSeq = 0;
      var allowedSignerRole = ${JSON.stringify(normalizeRole(allowedSignerRole) || null)};

      function post(message) {
        try {
          window.parent.postMessage(
            Object.assign({ ${JSON.stringify(ECONSENT_BUNDLE_HOST_MESSAGE_TAG)}: true }, message || {}),
            '*'
          );
        } catch (error) {
          // Ignore bridge failures so the bundle renderer keeps running.
        }
      }

      function overlayMatchesAllowedRole(overlay) {
        if (!allowedSignerRole) {
          return true;
        }
        var label = (overlay.getAttribute('aria-label') || overlay.textContent || '').toLowerCase();
        return (
          label.indexOf('role: ' + allowedSignerRole + '.') !== -1 ||
          label.indexOf('for ' + allowedSignerRole) !== -1
        );
      }

      function disableOtherSignerOverlays() {
        if (!allowedSignerRole) {
          return;
        }
        var overlays = document.querySelectorAll('.consent-signature-overlay');
        overlays.forEach(function (overlay) {
          if (overlayMatchesAllowedRole(overlay)) {
            return;
          }
          overlay.style.pointerEvents = 'none';
          overlay.style.cursor = 'not-allowed';
          overlay.style.opacity = '0.45';
          overlay.removeAttribute('role');
          overlay.setAttribute('aria-disabled', 'true');
        });
      }

      window.requestSignerName = function (request) {
        return new Promise(function (resolve) {
          var requestId = 'lumos_' + (++nextRequestSeq) + '_' + Date.now();
          pendingNameRequests[requestId] = resolve;
          post({ kind: 'request-name', requestId: requestId, request: request });
        });
      };

      window.addEventListener('message', function (event) {
        var data = event.data || {};
        if (data[${JSON.stringify(ECONSENT_BUNDLE_HOST_RESPONSE_TAG)}] !== true || data.kind !== 'name-response') {
          return;
        }
        var pending = pendingNameRequests[data.requestId];
        if (typeof pending !== 'function') {
          return;
        }
        delete pendingNameRequests[data.requestId];
        pending(typeof data.name === 'string' ? data.name : null);
      });

      window.sign = function (payload) {
        post({ kind: 'sign', signature: payload && payload.signature, name: payload && payload.name });
      };

      window.track = function (event) {
        post({ kind: 'track', event: event });
      };

      window.cancel = function () {
        post({ kind: 'cancel' });
      };

      function startOverlayObserver() {
        disableOtherSignerOverlays();
        if (document.body) {
          new MutationObserver(disableOtherSignerOverlays).observe(document.body, { childList: true, subtree: true });
        }
      }

      if (document.body) {
        startOverlayObserver();
      } else {
        document.addEventListener('DOMContentLoaded', startOverlayObserver, { once: true });
      }
    })();
`;
};
const injectHostHooksScript = (html, allowedSignerRole, signedSignatures) => {
    const scriptTag = `<script type="text/javascript">${getHostHooksCode(allowedSignerRole, signedSignatures)}</script>`;
    if (html.includes('</head>')) {
        return html.replace('</head>', `${scriptTag}</head>`);
    }
    return `<head>${scriptTag}</head>${html}`;
};
const createBundlePreview = async (bundleUrl, allowedSignerRole, signedSignatures) => {
    const response = await fetch(bundleUrl);
    if (!response.ok) {
        throw new Error('Unable to fetch consent bundle.');
    }
    const zip = await JSZip.loadAsync(await response.blob());
    const files = await Promise.all(Object.keys(zip.files).map(async (fileName) => {
        const zipEntry = zip.files[fileName];
        if (zipEntry.dir) {
            return null;
        }
        const extension = getBundleFileExtension(fileName);
        const isText = TEXT_EXTENSIONS.includes(extension || '');
        return {
            name: fileName,
            data: await zipEntry.async(isText ? 'string' : 'blob'),
            extension,
        };
    }));
    const bundleFiles = files.filter((file) => !!file);
    const html = bundleFiles.find((file) => file.name === 'index.html') ||
        bundleFiles.find((file) => file.name.endsWith('/index.html'));
    if (!html || typeof html.data !== 'string') {
        throw new Error('No index.html was found in the consent bundle.');
    }
    const urlByFileName = {};
    await Promise.all(bundleFiles
        .filter((file) => file.name !== html.name)
        .filter((file) => !TEXT_EXTENSIONS.includes(file.extension || ''))
        .map(async (file) => {
        urlByFileName[file.name] = await blobToDataUrl(file.data);
    }));
    await Promise.all(bundleFiles
        .filter((file) => file.name !== html.name)
        .filter((file) => TEXT_EXTENSIONS.includes(file.extension || ''))
        .map(async (file) => {
        const source = replaceBundleReferences(file.data, urlByFileName);
        urlByFileName[file.name] = await stringToDataUrl(source, getMimeType(file.extension));
    }));
    const htmlWithHooks = injectHostHooksScript(html.data, allowedSignerRole, signedSignatures);
    const previewHtml = replaceBundleReferences(htmlWithHooks, urlByFileName);
    return {
        previewHtml,
    };
};
export const ParticipantDocumentModal = ({ document, isOpen, onClose, }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const queryClient = useQueryClient();
    const account = useAxonAccountContext();
    const { environment } = useCurrentEnvironment();
    const { isFeatureEnabled } = useFeatureFlags();
    const isDocGenEconsentEnabled = isFeatureEnabled(SITE_FEATURE_FLAGS.DOC_GEN_ECONSENT);
    const iframeRef = useRef(null);
    const [previewHtml, setPreviewHtml] = useState();
    const [previewError, setPreviewError] = useState();
    const [password, setPassword] = useState('');
    const [printedName, setPrintedName] = useState(`${account.name.first} ${account.name.last}`.trim());
    const [initials, setInitials] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [signatureRequest, setSignatureRequest] = useState(null);
    const [signatureModalMode, setSignatureModalMode] = useState('sign');
    const { uploadSignedPdf } = useSignedPdfUpload({
        apiClient: environment ? getApiInstance(environment) : undefined,
        accountId: account._id,
    });
    const generateAndUploadSignedPdf = useCallback(async (docDetail) => {
        const docBundleUrl = docDetail.ec__published_studio_template?.ec__bundle?.url;
        if (!docBundleUrl || !environment) {
            return;
        }
        try {
            const summaries = getSignedSignatureSummaries(docDetail);
            if (!summaries.length) {
                return;
            }
            const { pdfBytes, signedOverlays } = await extractBundlePdfData(docBundleUrl, summaries);
            if (!signedOverlays.length) {
                return;
            }
            await uploadSignedPdf({
                pdfBytes,
                signatures: signedOverlays,
                signedDocId: docDetail._id,
                fileName: `${docDetail._id}_signed_consent.pdf`,
            });
        }
        catch (error) {
            console.error('Failed to generate/upload signed PDF:', error);
        }
    }, [environment, uploadSignedPdf]);
    const closeSignatureModal = () => {
        respondToSignatureRequest(null);
        setSignatureRequest(null);
    };
    const fetchDocumentDetail = async () => {
        if (!environment || !document?._id) {
            throw new Error('Document is unavailable.');
        }
        const response = await getApiInstance(environment).get(`/v2/account/${account._id}/ec__site_documents/${document._id}`, {
            params: {
                paths: DOCUMENT_DETAIL_PATHS,
                expand: ['ec__published_studio_template'],
                passive: true,
            },
        });
        return response.data?.data || response.data;
    };
    const { data: documentDetail, isFetching: isDocumentLoading, isError: isDocumentError, refetch: refetchDocumentDetail, } = useQuery({
        queryKey: ['participantDocumentDetail', document?._id],
        queryFn: fetchDocumentDetail,
        enabled: isOpen && !!document?._id && !!environment,
        refetchOnWindowFocus: false,
    });
    const bundleUrl = documentDetail?.ec__published_studio_template?.ec__bundle?.url;
    const currentSigner = useMemo(() => {
        return documentDetail?.ec__accepted_signers?.find((signer) => getSignerAccountId(signer) === account._id);
    }, [account._id, documentDetail]);
    const activeSignerRole = currentSigner?.ec__signer_role;
    const previewSignerRole = useMemo(() => {
        if (activeSignerRole) {
            return activeSignerRole;
        }
        return getRuntimeSignerRoles(documentDetail).find((role) => role.ec__signer_type === 'internal signer')?.ec__role;
    }, [activeSignerRole, documentDetail]);
    const signedSignatureIds = useMemo(() => getSignedSignatureIdentifiers(documentDetail), [documentDetail]);
    const signedSignatureSummaries = useMemo(() => getSignedSignatureSummaries(documentDetail), [documentDetail]);
    const previewFailedMessage = translate('error.participant_documents.preview_failed');
    useEffect(() => {
        let cancelled = false;
        setPreviewHtml(undefined);
        setPreviewError(undefined);
        if (!isOpen || !bundleUrl) {
            return;
        }
        createBundlePreview(bundleUrl, previewSignerRole, isDocGenEconsentEnabled ? signedSignatureSummaries : undefined)
            .then((preview) => {
            if (cancelled) {
                return;
            }
            setPreviewHtml(preview.previewHtml);
        })
            .catch(() => {
            if (!cancelled) {
                setPreviewError(previewFailedMessage);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [bundleUrl, isDocGenEconsentEnabled, isOpen, previewFailedMessage, previewSignerRole, signedSignatureSummaries]);
    useEffect(() => {
        if (!isOpen) {
            setPassword('');
            setInitials('');
            setPreviewError(undefined);
            setPreviewHtml(undefined);
            setSignatureRequest(null);
        }
    }, [isOpen]);
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.source !== iframeRef.current?.contentWindow || !isEconsentBundleHostMessage(event.data)) {
                return;
            }
            const { data } = event;
            if (data.kind === 'cancel') {
                onClose();
                return;
            }
            if (data.kind === 'track' || data.kind === 'sign') {
                return;
            }
            if (data.kind !== 'request-name') {
                return;
            }
            const requestedSignature = findSignatureByKey(documentDetail, data.request.ec__key);
            if (!requestedSignature) {
                iframeRef.current?.contentWindow?.postMessage(buildEconsentBundleNameResponse(data.requestId, null), '*');
                return;
            }
            if (signedSignatureIds.has(requestedSignature.ec__key)) {
                setSignatureRequest({
                    requestId: data.requestId,
                    request: {
                        ...data.request,
                        ec__key: requestedSignature.ec__key,
                        role: requestedSignature.ec__signer_role,
                    },
                });
                setSignatureModalMode('already-signed');
                return;
            }
            if (!activeSignerRole || (data.request?.role && !roleMatches(data.request.role, activeSignerRole))) {
                iframeRef.current?.contentWindow?.postMessage(buildEconsentBundleNameResponse(data.requestId, null), '*');
                return;
            }
            setSignatureRequest({
                requestId: data.requestId,
                request: data.request,
            });
            setSignatureModalMode('sign');
        };
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [activeSignerRole, documentDetail, signedSignatureIds]);
    const signingState = useMemo(() => {
        const completedDataIds = getCompletedDataIdentifiers(documentDetail);
        const signerRole = activeSignerRole;
        const signatures = getRuntimeSignatures(documentDetail).filter((signature) => roleMatches(signature.ec__signer_role, signerRole));
        const dataRequirements = getRuntimeData(documentDetail).filter((datum) => roleMatches(datum.ec__signer_role, signerRole));
        const pendingSignatures = signatures.filter((signature) => !signedSignatureIds.has(signature.ec__key));
        const pendingRequiredSignatures = pendingSignatures.filter((signature) => !signature.ec__optional);
        const pendingRequiredData = dataRequirements.filter((datum) => !datum.ec__optional && !completedDataIds.has(datum.ec__key));
        return {
            pendingSignatures,
            pendingRequiredData,
            pendingRequiredSignatures,
            requiresInitials: pendingSignatures.some((signature) => signature.ec__initials),
        };
    }, [activeSignerRole, documentDetail, signedSignatureIds]);
    function respondToSignatureRequest(name) {
        if (!signatureRequest || !iframeRef.current?.contentWindow) {
            return;
        }
        iframeRef.current.contentWindow.postMessage({
            ...buildEconsentBundleNameResponse(signatureRequest.requestId, name),
        }, '*');
    }
    const completeSignerIfReady = async (signedSignatureKey) => {
        if (!environment || !documentDetail || signingState.pendingRequiredData.length) {
            return;
        }
        const remainingRequiredSignatures = signingState.pendingRequiredSignatures.filter((signature) => signature.ec__key !== signedSignatureKey);
        if (remainingRequiredSignatures.length) {
            return;
        }
        await getApiInstance(environment).put('/routes/econsent/set_signer_complete', {
            ec__signed_document: documentDetail._id,
        });
    };
    const signSelectedSignature = async () => {
        if (!environment || !documentDetail || !activeSignerRole || !signatureRequest) {
            addNotification({
                title: '',
                subtitle: translate('error.participant_documents.not_active_signer'),
                type: 'error',
            });
            return;
        }
        const signature = findSignatureByKey(documentDetail, signatureRequest.request.ec__key);
        if (!signature) {
            addNotification({
                title: '',
                subtitle: translate('error.participant_documents.signature_field_not_found'),
                type: 'error',
            });
            return;
        }
        if (signedSignatureIds.has(signature.ec__key)) {
            respondToSignatureRequest(null);
            setSignatureRequest(null);
            addNotification({
                title: '',
                subtitle: translate('error.participant_documents.signature_already_captured'),
                type: 'error',
            });
            return;
        }
        setIsSubmitting(true);
        try {
            const api = getApiInstance(environment);
            const signedDate = new Date().toISOString();
            await api.post('/routes/econsent/sign', {
                ec__signed_document: documentDetail._id,
                username: account.email,
                password,
                ec__signature_identifier: signature.ec__key,
                signed_initials: signature.ec__initials ? initials : undefined,
                signed_name: printedName,
                signed_date: signedDate,
            });
            const shouldCompleteSigner = !signingState.pendingRequiredData.length &&
                signingState.pendingRequiredSignatures.every((requiredSignature) => requiredSignature.ec__key === signature.ec__key);
            await completeSignerIfReady(signature.ec__key);
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['participantDocuments'] }),
                queryClient.invalidateQueries({ queryKey: ['participantDocumentDetail', documentDetail._id] }),
            ]);
            const { data: refreshedDetail } = await refetchDocumentDetail();
            respondToSignatureRequest(printedName);
            setSignatureRequest(null);
            setPassword('');
            setInitials('');
            addNotification({
                title: '',
                subtitle: translate('info.participant_documents.signature_captured'),
                type: 'success',
            });
            if (shouldCompleteSigner && refreshedDetail) {
                void generateAndUploadSignedPdf(refreshedDetail);
                onClose();
            }
            else if (shouldCompleteSigner) {
                onClose();
            }
        }
        catch (error) {
            addNotification({
                title: '',
                subtitle: getApiErrorMessage(error, true) || translate('error.participant_documents.sign_failed'),
                type: 'error',
            });
        }
        finally {
            setIsSubmitting(false);
        }
    };
    if (!document) {
        return null;
    }
    const isAlreadySignedModal = signatureModalMode === 'already-signed';
    const isComplete = currentSigner?.ec__status === 'complete' || documentDetail?.ec__status === 'complete';
    const documentTitle = getDisplayTitle(documentDetail?.ec__document_template?.ec__title || document.ec__document_template.ec__title);
    const canSubmit = !!activeSignerRole &&
        !isComplete &&
        !!printedName &&
        !!password &&
        (!signingState.requiresInitials || !!initials) &&
        !!signatureRequest;
    return (_jsxs(_Fragment, { children: [_jsx(ModalWindow, { title: isDocGenEconsentEnabled
                    ? translate('label.participant_documents.consent_document')
                    : document.ec__document_template.ec__title, subtitle: isDocGenEconsentEnabled ? (_jsx(Text, { size: 'sm', className: 'truncate text-neutral-600', title: documentTitle, children: documentTitle })) : undefined, isOpen: isOpen, closeWindow: onClose, width: 'lg', removeBodyPadding: true, dataTestId: 'participant-document-modal', footerSecondaryActionButtons: [
                    {
                        label: isDocGenEconsentEnabled && isComplete ? translate('generic.close') : translate('generic.cancel'),
                        onClick: onClose,
                        disabled: isSubmitting,
                    },
                ], children: _jsx("div", { className: `flex justify-center bg-neutral-50 p-4 ${isDocGenEconsentEnabled ? 'h-[calc(80vh-10.25rem)] min-h-0' : 'h-[78vh]'}`, children: isDocumentLoading ? (_jsx(Spinner, { wrapper: 'full' })) : isDocumentError ? (_jsx(Text, { className: 'text-red-600', children: translate('error.participant_documents.load_failed') })) : (_jsx("div", { className: `h-full w-full max-w-[760px] overflow-hidden rounded border border-neutral-300 bg-white ${isDocGenEconsentEnabled ? 'min-h-0' : ''}`, children: previewHtml ? (_jsx("iframe", { ref: iframeRef, srcDoc: previewHtml, sandbox: 'allow-scripts', title: documentTitle, className: 'h-full w-full border-0', "data-testid": 'participant-document-preview' })) : previewError ? (_jsx("div", { className: 'p-6 text-center text-red-600', children: previewError })) : (_jsx(Spinner, { wrapper: 'full' })) })) }) }), _jsx(ModalWindow, { title: isAlreadySignedModal
                    ? translate('label.participant_documents.signature_status')
                    : isDocGenEconsentEnabled
                        ? translate('label.participant_documents.sign_consent')
                        : document.ec__document_template.ec__title, subtitle: isDocGenEconsentEnabled && !isAlreadySignedModal ? (_jsx(Text, { size: 'sm', className: 'truncate text-neutral-600', title: documentTitle, children: documentTitle })) : undefined, isOpen: !!signatureRequest, closeWindow: closeSignatureModal, width: isAlreadySignedModal ? 'base' : 'lg', dataTestId: 'participant-document-signature-modal', footerSecondaryActionButtons: [
                    {
                        label: isAlreadySignedModal ? translate('generic.close') : translate('generic.cancel'),
                        onClick: closeSignatureModal,
                        disabled: isSubmitting,
                    },
                ], footerPrimaryActionButton: isAlreadySignedModal
                    ? undefined
                    : {
                        label: isSubmitting ? translate('action.pi_oversight.signing') : translate('action.pi_oversight.sign'),
                        onClick: signSelectedSignature,
                        variant: 'primary',
                        disabled: !canSubmit || isSubmitting,
                    }, children: _jsx("div", { className: `flex justify-center ${isAlreadySignedModal ? 'py-2' : 'pb-6 pt-8'}`, children: _jsxs("div", { className: 'flex w-full max-w-[560px] flex-col gap-6', children: [isAlreadySignedModal ? (_jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx(Text, { className: 'text-lg font-semibold text-neutral-900', children: translate('label.participant_documents.signature_already_captured') }), _jsx(Text, { className: 'text-sm text-neutral-600', children: translate('info.participant_documents.signature_already_completed') })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx(Text, { className: 'text-2xl font-semibold text-neutral-900', children: translate('label.pi_oversight.sign_confirmation_heading') }), _jsx(Text, { className: 'text-lg text-neutral-600', children: translate('label.pi_oversight.sign_confirmation') })] }), _jsx(Input, { id: 'participant-document-printed-name', label: translate('label.participant_documents.printed_name'), value: printedName, onChange: (event) => setPrintedName(event.target.value), disabled: isSubmitting || isComplete, width: 'full', required: true }), _jsx(Input, { id: 'participant-document-email', label: translate('generic.email'), value: account.email, disabled: true, width: 'full' }), signingState.requiresInitials && (_jsx(Input, { id: 'participant-document-initials', label: translate('label.participant_documents.initials'), value: initials, onChange: (event) => setInitials(event.target.value), disabled: isSubmitting || isComplete, width: 'full', required: true })), _jsxs("div", { className: 'flex flex-col gap-2', children: [_jsxs("div", { className: 'flex items-center justify-between', children: [_jsx(Text, { className: 'text-sm font-medium text-neutral-900', children: translate('generic.password') }), _jsx("button", { className: 'text-sm font-medium text-primary-600 underline', type: 'button', children: translate('generic.forgot_password') })] }), _jsx(Input, { id: 'participant-document-password', label: '', type: 'password', value: password, onChange: (event) => setPassword(event.target.value), disabled: isSubmitting || isComplete, width: 'full', required: true })] })] })), !activeSignerRole && (_jsx(Text, { className: 'text-red-600', children: translate('error.participant_documents.not_active_signer') })), !isAlreadySignedModal && !!signingState.pendingRequiredData.length && (_jsx(Text, { className: 'text-red-600', children: translate('error.participant_documents.required_data_pending') })), !isAlreadySignedModal && isComplete && (_jsx(Text, { children: translate('info.participant_documents.document_completed') }))] }) }) })] }));
};
